import React, { useState, useEffect } from 'react';
import { Search, Activity, Clock, CheckCircle2, XCircle, BarChart3, TrendingUp, RefreshCw, Filter, ChevronRight, Layers, Cpu, Zap, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

export default function SearchDiagnostics() {
  const [overview, setOverview] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ hours: 24, status: '', plan: '' });
  const [activeTab, setActiveTab] = useState('overview');

  const fetchOverview = async () => {
    try {
      const res = await api.get(`/api/admin/search-diagnostics/overview?hours=${filters.hours}`);
      setOverview(res.data);
    } catch {}
  };

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams({ hours: filters.hours, limit: 100 });
      if (filters.status) params.append('status', filters.status);
      if (filters.plan) params.append('plan', filters.plan);
      const res = await api.get(`/api/admin/search-diagnostics/jobs?${params}`);
      setJobs(res.data.jobs || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchJobDetail = async (jobId) => {
    try {
      const res = await api.get(`/api/admin/search-diagnostics/jobs/${jobId}`);
      setJobDetail(res.data);
    } catch {}
  };

  useEffect(() => { fetchOverview(); fetchJobs(); }, []);
  useEffect(() => { fetchOverview(); fetchJobs(); }, [filters]);

  const handleJobClick = (job) => {
    setSelectedJob(job.job_id);
    fetchJobDetail(job.job_id);
    setActiveTab('overview');
  };

  const formatDuration = (sec) => {
    if (!sec) return ', ';
    if (sec < 60) return `${sec.toFixed(1)}s`;
    return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  };

  const formatTime = (iso) => {
    if (!iso) return ', ';
    return new Date(iso).toLocaleTimeString();
  };

  const stageBars = (job) => {
    const stages = [
      { label: 'Scrape', sec: job.scrape_duration_sec, color: 'var(--accent-primary)' },
      { label: 'Enrich', sec: job.enrich_duration_sec, color: 'var(--success)' },
      { label: 'Score', sec: job.score_duration_sec, color: 'var(--warning)' },
      { label: 'Save', sec: job.save_duration_sec, color: 'var(--info)' },
    ];
    const max = Math.max(...stages.map(s => s.sec || 0), 1);
    return (
      <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden', marginTop: 6 }}>
        {stages.map((s, i) => (
          <div key={i} title={`${s.label}: ${formatDuration(s.sec)}`} style={{
            flex: s.sec || 0.1, background: s.color, minWidth: s.sec > 0 ? 8 : 0,
            transition: 'flex 0.3s ease',
          }} />
        ))}
      </div>
    );
  };

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={22} color="var(--accent-primary)" /> Search Diagnostics
          </h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 0 }}>
            Real-time search performance and quality monitoring
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={filters.hours} onChange={e => setFilters({...filters, hours: parseInt(e.target.value)})} style={filterSelect}>
            <option value={1}>Last hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
          <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} style={filterSelect}>
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button onClick={() => { fetchOverview(); fetchJobs(); }} style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-primary)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
          }}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Searches', value: overview.total_searches, icon: <Search size={18} />, color: 'var(--accent-primary)' },
            { label: 'Running', value: overview.running, icon: <Activity size={18} />, color: 'var(--info)' },
            { label: 'Failed', value: overview.failed, icon: <XCircle size={18} />, color: overview.failed > 0 ? 'var(--error)' : 'var(--success)' },
            { label: 'Success Rate', value: `${overview.success_rate}%`, icon: <CheckCircle2 size={18} />, color: 'var(--success)' },
            { label: 'Avg Duration', value: formatDuration(overview.avg_duration_sec), icon: <Clock size={18} />, color: 'var(--warning)' },
            { label: 'Avg Time/Lead', value: formatDuration(overview.avg_time_per_lead), icon: <Zap size={18} />, color: 'var(--accent-secondary)' },
          ].map((card, i) => (
            <div key={i} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ color: card.color, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>{card.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{card.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Job List + Detail */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Left: Job List */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, overflow: 'hidden', maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers size={14} /> Search Jobs ({jobs.length})
          </div>
          {jobs.map((job, i) => (
            <div key={i} onClick={() => handleJobClick(job)} style={{
              padding: '14px 18px', borderBottom: '1px solid var(--border-primary)',
              cursor: 'pointer', background: selectedJob === job.job_id ? 'var(--accent-glow)' : 'transparent',
              transition: 'background 0.15s ease',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                    {job.search_query || 'Unknown query'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {job.user_email || 'Unknown'} · {job.leads_found || 0} leads
                  </div>
                </div>
                <span style={{
                  padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                  background: job.job_status === 'completed' ? 'var(--success-bg)' : job.job_status === 'failed' ? 'var(--error-bg)' : 'var(--info-bg)',
                  color: job.job_status === 'completed' ? 'var(--success)' : job.job_status === 'failed' ? 'var(--error)' : 'var(--info)',
                }}>{job.job_status || job.status || 'unknown'}</span>
              </div>
              {stageBars(job)}
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                {formatDuration(job.total_duration_sec)} · {formatTime(job.scrape_started_at)}
              </div>
            </div>
          ))}
          {jobs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)', fontSize: 13 }}>No search jobs found</div>
          )}
        </div>

        {/* Right: Job Detail */}
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 16, padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
          {jobDetail ? (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>
                {jobDetail.search_query || 'Unknown'}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 16px' }}>
                {jobDetail.email || jobDetail.user_email} · {jobDetail.plan || 'free'} plan · {jobDetail.leads_found || 0} leads
              </p>

              {/* Stage Tabs */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {['overview', 'scrape', 'enrich', 'score'].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)} style={{
                    padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    background: activeTab === tab ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                    color: activeTab === tab ? 'white' : 'var(--text-secondary)',
                    fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  }}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <DetailRow label="Total Duration" value={formatDuration(jobDetail.total_duration_sec)} />
                  <DetailRow label="Scrape" value={formatDuration(jobDetail.scrape_duration_sec)} />
                  <DetailRow label="Enrich" value={formatDuration(jobDetail.enrich_duration_sec)} />
                  <DetailRow label="Score" value={formatDuration(jobDetail.score_duration_sec)} />
                  <DetailRow label="Save" value={formatDuration(jobDetail.save_duration_sec)} />
                  <DetailRow label="Enrich Success" value={`${jobDetail.enrich_success_count || 0}/${jobDetail.leads_found || 0}`} />
                  <DetailRow label="Score Success" value={`${jobDetail.score_success_count || 0}/${jobDetail.leads_found || 0}`} />
                  <DetailRow label="Status" value={jobDetail.status || jobDetail.job_status || 'unknown'} />
                </div>
              )}

              {activeTab === 'scrape' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <DetailRow label="Duration" value={formatDuration(jobDetail.scrape_duration_sec)} />
                  <DetailRow label="Started" value={formatTime(jobDetail.scrape_started_at)} />
                  <DetailRow label="Completed" value={formatTime(jobDetail.scrape_completed_at)} />
                  <DetailRow label="Leads Found" value={jobDetail.leads_found || 0} />
                </div>
              )}

              {activeTab === 'enrich' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <DetailRow label="Duration" value={formatDuration(jobDetail.enrich_duration_sec)} />
                  <DetailRow label="Success" value={`${jobDetail.enrich_success_count || 0}/${jobDetail.leads_found || 0}`} />
                </div>
              )}

              {activeTab === 'score' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <DetailRow label="Duration" value={formatDuration(jobDetail.score_duration_sec)} />
                  <DetailRow label="Success" value={`${jobDetail.score_success_count || 0}/${jobDetail.leads_found || 0}`} />
                  {jobDetail.scored_leads && jobDetail.scored_leads.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Score Distribution</div>
                      {jobDetail.scored_leads.map((l, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 11 }}>
                          <span style={{ color: 'var(--text-primary)' }}>{l.company_name?.slice(0, 30)}</span>
                          <span style={{ fontWeight: 600, color: l.lead_score >= 70 ? 'var(--success)' : l.lead_score >= 40 ? 'var(--warning)' : 'var(--error)' }}>
                            {l.lead_score}/100
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}>
              <BarChart3 size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p style={{ fontSize: 13 }}>Select a job to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{value || ', '}</span>
    </div>
  );
}

const filterSelect = {
  padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-primary)',
  background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12,
  fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
};
